/**
 * Local ingredient substitution database
 * Covers ~50 common ingredients with cooking-appropriate alternatives
 */

export interface Substitute {
  name: string;
  ratio: string;
  notes?: string;
}

export interface IngredientSubstitution {
  substitutes: Substitute[];
  category: string;
}

export const ingredientSubstitutions: Record<string, IngredientSubstitution> = {
  // DAIRY
  butter: {
    category: 'dairy',
    substitutes: [
      { name: 'coconut oil', ratio: '1:1', notes: 'Best for baking' },
      { name: 'olive oil', ratio: '3/4 cup per 1 cup', notes: 'Savory dishes only' },
      { name: 'applesauce', ratio: '1:1', notes: 'Reduces fat, adds moisture' },
      { name: 'avocado', ratio: '1:1', notes: 'Creamy texture, baking' },
      { name: 'Greek yogurt', ratio: '1/2 cup per 1 cup', notes: 'Adds protein' },
    ],
  },
  milk: {
    category: 'dairy',
    substitutes: [
      { name: 'oat milk', ratio: '1:1', notes: 'Best all-purpose substitute' },
      { name: 'almond milk', ratio: '1:1', notes: 'Lighter, slightly nutty' },
      { name: 'coconut milk', ratio: '1:1', notes: 'Richer, tropical flavor' },
      { name: 'soy milk', ratio: '1:1', notes: 'High protein' },
      { name: 'water + butter', ratio: '1 cup water + 1 tbsp butter', notes: 'For baking' },
    ],
  },
  cream: {
    category: 'dairy',
    substitutes: [
      { name: 'coconut cream', ratio: '1:1', notes: 'Rich and creamy' },
      { name: 'cashew cream', ratio: '1:1', notes: 'Blend soaked cashews' },
      { name: 'silken tofu', ratio: '1:1 blended', notes: 'High protein option' },
      { name: 'evaporated milk', ratio: '1:1', notes: 'Less rich than cream' },
    ],
  },
  'heavy cream': {
    category: 'dairy',
    substitutes: [
      { name: 'coconut cream', ratio: '1:1', notes: 'Best for whipping' },
      { name: 'milk + butter', ratio: '3/4 cup milk + 1/4 cup melted butter', notes: 'For cooking' },
      { name: 'Greek yogurt', ratio: '1:1', notes: 'Tangy, thick' },
    ],
  },
  'sour cream': {
    category: 'dairy',
    substitutes: [
      { name: 'Greek yogurt', ratio: '1:1', notes: 'Nearly identical' },
      { name: 'cottage cheese', ratio: '1:1 blended', notes: 'Blend until smooth' },
      { name: 'coconut cream + lemon', ratio: '1 cup + 1 tbsp lemon', notes: 'Dairy-free' },
    ],
  },
  yogurt: {
    category: 'dairy',
    substitutes: [
      { name: 'sour cream', ratio: '1:1', notes: 'Similar tang' },
      { name: 'coconut yogurt', ratio: '1:1', notes: 'Dairy-free' },
      { name: 'buttermilk', ratio: '1:1', notes: 'Thinner consistency' },
      { name: 'silken tofu + lemon', ratio: '1:1', notes: 'Blend with 1 tbsp lemon' },
    ],
  },
  cheese: {
    category: 'dairy',
    substitutes: [
      { name: 'nutritional yeast', ratio: '1/4 cup per 1 cup', notes: 'Cheesy flavor, no melt' },
      { name: 'cashew cheese', ratio: '1:1', notes: 'Blend soaked cashews' },
      { name: 'tofu', ratio: '1:1 crumbled', notes: 'For ricotta-style' },
    ],
  },
  parmesan: {
    category: 'dairy',
    substitutes: [
      { name: 'nutritional yeast', ratio: '3 tbsp per 1/4 cup', notes: 'Umami flavor' },
      { name: 'pecorino romano', ratio: '1:1', notes: 'Sharper, saltier' },
      { name: 'asiago', ratio: '1:1', notes: 'Similar texture' },
    ],
  },

  // EGGS & BINDING
  egg: {
    category: 'binding',
    substitutes: [
      { name: 'flax egg', ratio: '1 tbsp ground flax + 3 tbsp water', notes: 'Let sit 5 min' },
      { name: 'chia egg', ratio: '1 tbsp chia + 3 tbsp water', notes: 'Let sit 5 min' },
      { name: 'banana', ratio: '1/4 cup mashed', notes: 'Adds sweetness' },
      { name: 'applesauce', ratio: '1/4 cup', notes: 'For binding in baking' },
      { name: 'silken tofu', ratio: '1/4 cup blended', notes: 'Neutral flavor' },
      { name: 'aquafaba', ratio: '3 tbsp', notes: 'Chickpea water, great for meringue' },
    ],
  },
  eggs: {
    category: 'binding',
    substitutes: [
      { name: 'flax eggs', ratio: '1 tbsp ground flax + 3 tbsp water per egg', notes: 'Let sit 5 min' },
      { name: 'chia eggs', ratio: '1 tbsp chia + 3 tbsp water per egg', notes: 'Let sit 5 min' },
      { name: 'banana', ratio: '1/4 cup mashed per egg', notes: 'Adds sweetness' },
      { name: 'applesauce', ratio: '1/4 cup per egg', notes: 'For binding' },
    ],
  },
  breadcrumbs: {
    category: 'binding',
    substitutes: [
      { name: 'oats', ratio: '1:1 blended', notes: 'Pulse in food processor' },
      { name: 'crushed crackers', ratio: '1:1', notes: 'Saltier option' },
      { name: 'almond flour', ratio: '1:1', notes: 'Gluten-free, nutty' },
      { name: 'panko', ratio: '1:1', notes: 'Lighter, crispier' },
    ],
  },

  // PROTEINS
  chicken: {
    category: 'protein',
    substitutes: [
      { name: 'tofu', ratio: '1:1 pressed', notes: 'Press to remove water' },
      { name: 'turkey', ratio: '1:1', notes: 'Very similar' },
      { name: 'tempeh', ratio: '1:1', notes: 'Nutty, firm texture' },
      { name: 'seitan', ratio: '1:1', notes: 'Wheat-based, chewy' },
      { name: 'jackfruit', ratio: '1:1 shredded', notes: 'For pulled/shredded dishes' },
    ],
  },
  beef: {
    category: 'protein',
    substitutes: [
      { name: 'mushrooms', ratio: '1:1 chopped', notes: 'Portobello for steaks' },
      { name: 'lentils', ratio: '1:1 cooked', notes: 'For ground beef dishes' },
      { name: 'tempeh', ratio: '1:1 crumbled', notes: 'High protein' },
      { name: 'black beans', ratio: '1:1', notes: 'For tacos, chili' },
      { name: 'Beyond/Impossible meat', ratio: '1:1', notes: 'Most similar texture' },
    ],
  },
  'ground beef': {
    category: 'protein',
    substitutes: [
      { name: 'lentils', ratio: '1:1 cooked', notes: 'Brown or green lentils' },
      { name: 'mushrooms + walnuts', ratio: '1:1 finely chopped', notes: 'Blend together' },
      { name: 'tempeh', ratio: '1:1 crumbled', notes: 'Crumble and brown' },
      { name: 'TVP', ratio: '1:1 rehydrated', notes: 'Textured vegetable protein' },
    ],
  },
  pork: {
    category: 'protein',
    substitutes: [
      { name: 'jackfruit', ratio: '1:1', notes: 'Great for pulled pork' },
      { name: 'mushrooms', ratio: '1:1', notes: 'King oyster for bacon-like' },
      { name: 'seitan', ratio: '1:1', notes: 'Chewy, meaty texture' },
      { name: 'chicken', ratio: '1:1', notes: 'Similar cooking methods' },
    ],
  },
  bacon: {
    category: 'protein',
    substitutes: [
      { name: 'tempeh bacon', ratio: '1:1 sliced thin', notes: 'Marinate in soy + maple' },
      { name: 'coconut bacon', ratio: '1:1', notes: 'Crispy, smoky' },
      { name: 'mushroom bacon', ratio: '1:1 sliced', notes: 'Shiitake works well' },
      { name: 'turkey bacon', ratio: '1:1', notes: 'Lower fat' },
    ],
  },
  fish: {
    category: 'protein',
    substitutes: [
      { name: 'tofu', ratio: '1:1 firm', notes: 'Press well, marinate' },
      { name: 'hearts of palm', ratio: '1:1 shredded', notes: 'For crab/fish cakes' },
      { name: 'jackfruit', ratio: '1:1', notes: 'Flaky texture when cooked' },
      { name: 'banana blossom', ratio: '1:1', notes: 'Fish and chips style' },
    ],
  },
  salmon: {
    category: 'protein',
    substitutes: [
      { name: 'carrots', ratio: '1:1 roasted', notes: 'Smoked carrot lox' },
      { name: 'trout', ratio: '1:1', notes: 'Similar flavor, texture' },
      { name: 'arctic char', ratio: '1:1', notes: 'Milder flavor' },
    ],
  },
  shrimp: {
    category: 'protein',
    substitutes: [
      { name: 'king oyster mushrooms', ratio: '1:1 sliced', notes: 'Similar texture' },
      { name: 'hearts of palm', ratio: '1:1', notes: 'Mild, tender' },
      { name: 'konjac', ratio: '1:1', notes: 'Chewy texture' },
    ],
  },

  // FLOUR & GRAINS
  'all-purpose flour': {
    category: 'flour',
    substitutes: [
      { name: 'whole wheat flour', ratio: '1:1', notes: 'Denser, nuttier' },
      { name: 'almond flour', ratio: '1:1', notes: 'Gluten-free, denser' },
      { name: 'oat flour', ratio: '1:1', notes: 'Blend oats until fine' },
      { name: 'coconut flour', ratio: '1/4 cup per 1 cup', notes: 'Very absorbent' },
      { name: 'gluten-free flour blend', ratio: '1:1', notes: 'Check brand instructions' },
    ],
  },
  flour: {
    category: 'flour',
    substitutes: [
      { name: 'whole wheat flour', ratio: '1:1', notes: 'Denser result' },
      { name: 'almond flour', ratio: '1:1', notes: 'Gluten-free' },
      { name: 'oat flour', ratio: '1:1', notes: 'Blend rolled oats' },
      { name: 'rice flour', ratio: '1:1', notes: 'Lighter texture' },
    ],
  },
  rice: {
    category: 'grain',
    substitutes: [
      { name: 'quinoa', ratio: '1:1', notes: 'Higher protein' },
      { name: 'cauliflower rice', ratio: '1:1', notes: 'Low carb option' },
      { name: 'bulgur', ratio: '1:1', notes: 'Nutty flavor' },
      { name: 'couscous', ratio: '1:1', notes: 'Faster cooking' },
      { name: 'barley', ratio: '1:1', notes: 'Chewy texture' },
    ],
  },
  pasta: {
    category: 'grain',
    substitutes: [
      { name: 'zucchini noodles', ratio: '1:1', notes: 'Low carb, spiralize' },
      { name: 'spaghetti squash', ratio: '1:1', notes: 'Roast and scrape' },
      { name: 'rice noodles', ratio: '1:1', notes: 'Gluten-free' },
      { name: 'shirataki noodles', ratio: '1:1', notes: 'Very low calorie' },
      { name: 'lentil pasta', ratio: '1:1', notes: 'High protein' },
    ],
  },

  // FATS & OILS
  'vegetable oil': {
    category: 'fat',
    substitutes: [
      { name: 'canola oil', ratio: '1:1', notes: 'Neutral flavor' },
      { name: 'coconut oil', ratio: '1:1', notes: 'Slight coconut taste' },
      { name: 'olive oil', ratio: '1:1', notes: 'For lower heat cooking' },
      { name: 'avocado oil', ratio: '1:1', notes: 'High smoke point' },
      { name: 'applesauce', ratio: '1:1', notes: 'For baking, reduces fat' },
    ],
  },
  'olive oil': {
    category: 'fat',
    substitutes: [
      { name: 'avocado oil', ratio: '1:1', notes: 'Higher smoke point' },
      { name: 'coconut oil', ratio: '1:1', notes: 'For high heat' },
      { name: 'butter', ratio: '1:1', notes: 'Richer flavor' },
      { name: 'grapeseed oil', ratio: '1:1', notes: 'Neutral, high heat' },
    ],
  },
  'coconut oil': {
    category: 'fat',
    substitutes: [
      { name: 'butter', ratio: '1:1', notes: 'Similar texture when solid' },
      { name: 'vegetable oil', ratio: '1:1', notes: 'Neutral flavor' },
      { name: 'avocado oil', ratio: '1:1', notes: 'For high heat' },
    ],
  },

  // SWEETENERS
  sugar: {
    category: 'sweetener',
    substitutes: [
      { name: 'honey', ratio: '3/4 cup per 1 cup', notes: 'Reduce liquid in recipe' },
      { name: 'maple syrup', ratio: '3/4 cup per 1 cup', notes: 'Reduce liquid slightly' },
      { name: 'coconut sugar', ratio: '1:1', notes: 'Lower glycemic' },
      { name: 'stevia', ratio: '1 tsp per 1 cup', notes: 'Very concentrated' },
      { name: 'monk fruit', ratio: '1:1 for baking blends', notes: 'Zero calorie' },
    ],
  },
  'brown sugar': {
    category: 'sweetener',
    substitutes: [
      { name: 'white sugar + molasses', ratio: '1 cup + 1 tbsp molasses', notes: 'DIY brown sugar' },
      { name: 'coconut sugar', ratio: '1:1', notes: 'Similar caramel notes' },
      { name: 'maple syrup', ratio: '3/4 cup per 1 cup', notes: 'Adjust liquid' },
    ],
  },
  honey: {
    category: 'sweetener',
    substitutes: [
      { name: 'maple syrup', ratio: '1:1', notes: 'Vegan option' },
      { name: 'agave nectar', ratio: '1:1', notes: 'Milder flavor' },
      { name: 'brown rice syrup', ratio: '1:1', notes: 'Less sweet' },
      { name: 'date syrup', ratio: '1:1', notes: 'Rich, caramel-like' },
    ],
  },
  'maple syrup': {
    category: 'sweetener',
    substitutes: [
      { name: 'honey', ratio: '1:1', notes: 'Thicker, sweeter' },
      { name: 'agave nectar', ratio: '1:1', notes: 'Thinner, milder' },
      { name: 'brown sugar + water', ratio: '3/4 cup + 1/4 cup water', notes: 'Simmer to dissolve' },
    ],
  },

  // COMMON ALLERGENS & SPECIAL
  'soy sauce': {
    category: 'condiment',
    substitutes: [
      { name: 'coconut aminos', ratio: '1:1', notes: 'Soy-free, sweeter' },
      { name: 'tamari', ratio: '1:1', notes: 'Gluten-free soy sauce' },
      { name: 'liquid aminos', ratio: '1:1', notes: 'Soy-based, less sodium' },
      { name: 'fish sauce + water', ratio: '1/2 fish sauce + 1/2 water', notes: 'Umami flavor' },
    ],
  },
  'fish sauce': {
    category: 'condiment',
    substitutes: [
      { name: 'soy sauce + lime', ratio: '1 tbsp soy + 1/2 tsp lime', notes: 'Add dash of sugar' },
      { name: 'coconut aminos', ratio: '1:1', notes: 'Sweeter, milder' },
      { name: 'miso paste', ratio: '1 tsp per 1 tbsp', notes: 'Dilute with water' },
      { name: 'seaweed + soy sauce', ratio: '1 tbsp soy + pinch seaweed', notes: 'Ocean flavor' },
    ],
  },
  peanuts: {
    category: 'nut',
    substitutes: [
      { name: 'sunflower seeds', ratio: '1:1', notes: 'Nut-free option' },
      { name: 'almonds', ratio: '1:1', notes: 'If not allergic to tree nuts' },
      { name: 'soy nuts', ratio: '1:1', notes: 'Similar crunch' },
      { name: 'pumpkin seeds', ratio: '1:1', notes: 'Nut-free' },
    ],
  },
  'peanut butter': {
    category: 'nut',
    substitutes: [
      { name: 'sunflower seed butter', ratio: '1:1', notes: 'Nut-free, may turn green in baking' },
      { name: 'almond butter', ratio: '1:1', notes: 'If tree nuts OK' },
      { name: 'tahini', ratio: '1:1', notes: 'Sesame-based' },
      { name: 'soy nut butter', ratio: '1:1', notes: 'Nut-free' },
    ],
  },
  garlic: {
    category: 'aromatic',
    substitutes: [
      { name: 'garlic powder', ratio: '1/8 tsp per clove', notes: 'Concentrated flavor' },
      { name: 'shallots', ratio: '1/2 shallot per clove', notes: 'Milder, sweeter' },
      { name: 'garlic-infused oil', ratio: '1/2 tsp per clove', notes: 'Low FODMAP option' },
      { name: 'chives', ratio: '1 tbsp per clove', notes: 'Mild onion-garlic flavor' },
    ],
  },
  onion: {
    category: 'aromatic',
    substitutes: [
      { name: 'shallots', ratio: '3 shallots per 1 onion', notes: 'Milder, sweeter' },
      { name: 'leeks', ratio: '1 leek per 1 onion', notes: 'Use white part only' },
      { name: 'green onions', ratio: '1 bunch per 1 onion', notes: 'Milder flavor' },
      { name: 'onion powder', ratio: '1 tbsp per 1 medium onion', notes: 'For convenience' },
      { name: 'celery', ratio: '2 stalks per 1 onion', notes: 'Different but adds aromatics' },
    ],
  },
  'lemon juice': {
    category: 'acid',
    substitutes: [
      { name: 'lime juice', ratio: '1:1', notes: 'Slightly different flavor' },
      { name: 'white wine vinegar', ratio: '1/2 amount', notes: 'More acidic' },
      { name: 'apple cider vinegar', ratio: '1/2 amount', notes: 'Fruity tang' },
      { name: 'orange juice', ratio: '1:1', notes: 'Sweeter, less acidic' },
    ],
  },
  wine: {
    category: 'liquid',
    substitutes: [
      { name: 'broth', ratio: '1:1', notes: 'Add splash of vinegar' },
      { name: 'grape juice + vinegar', ratio: '3/4 cup juice + 1 tbsp vinegar', notes: 'For red wine' },
      { name: 'apple juice + vinegar', ratio: '3/4 cup juice + 1 tbsp vinegar', notes: 'For white wine' },
    ],
  },
  // Shaoxing wine and dry sherry are each other's standard stand-in; the pair is
  // why "2 tablespoons Shaoxing rice wine or dry sherry" is written that way in
  // the first place. Before these entries the line fell through to `rice` and
  // the sheet offered quinoa.
  'rice wine': {
    category: 'liquid',
    substitutes: [
      { name: 'dry sherry', ratio: '1:1', notes: 'The standard swap' },
      { name: 'mirin', ratio: '1:1', notes: 'Sweeter — cut any added sugar' },
      { name: 'dry vermouth', ratio: '1:1', notes: 'Drier, works in stir-fries' },
      { name: 'chicken broth + rice vinegar', ratio: '1 tbsp broth + 1 tsp vinegar', notes: 'Alcohol-free' },
    ],
  },
  sherry: {
    category: 'liquid',
    substitutes: [
      { name: 'rice wine', ratio: '1:1', notes: 'Shaoxing is the usual pair' },
      { name: 'dry vermouth', ratio: '1:1', notes: 'Similar dryness' },
      { name: 'white wine', ratio: '1:1', notes: 'Lighter, less nutty' },
      { name: 'apple cider vinegar + water', ratio: '1 tsp + 1 tbsp', notes: 'Alcohol-free' },
    ],
  },
  'red wine': {
    category: 'liquid',
    substitutes: [
      { name: 'beef broth', ratio: '1:1', notes: 'Add 1 tbsp vinegar' },
      { name: 'grape juice', ratio: '1:1', notes: 'Sweeter, add vinegar' },
      { name: 'pomegranate juice', ratio: '1:1', notes: 'Tangy, rich color' },
      { name: 'cranberry juice', ratio: '1:1', notes: 'Tart alternative' },
    ],
  },
  'white wine': {
    category: 'liquid',
    substitutes: [
      { name: 'chicken broth', ratio: '1:1', notes: 'Add 1 tsp lemon juice' },
      { name: 'apple cider vinegar + water', ratio: '1 tbsp + 1 cup water', notes: 'For acidity' },
      { name: 'white grape juice', ratio: '1:1', notes: 'Sweeter option' },
      { name: 'dry vermouth', ratio: '1:1', notes: 'If alcohol OK' },
    ],
  },
  cornstarch: {
    category: 'thickener',
    substitutes: [
      { name: 'arrowroot', ratio: '1:1', notes: 'Clear, glossy result' },
      { name: 'tapioca starch', ratio: '2 tbsp per 1 tbsp cornstarch', notes: 'More chewy' },
      { name: 'all-purpose flour', ratio: '2 tbsp per 1 tbsp cornstarch', notes: 'Opaque result' },
      { name: 'potato starch', ratio: '1:1', notes: 'Good for high heat' },
    ],
  },
  'baking powder': {
    category: 'leavening',
    substitutes: [
      { name: 'baking soda + cream of tartar', ratio: '1/4 tsp soda + 1/2 tsp cream of tartar per 1 tsp', notes: 'DIY baking powder' },
      { name: 'baking soda + lemon juice', ratio: '1/4 tsp soda + 1/2 tsp lemon per 1 tsp', notes: 'Adds slight tang' },
      { name: 'baking soda + vinegar', ratio: '1/4 tsp soda + 1/2 tsp vinegar per 1 tsp', notes: 'Neutral taste' },
    ],
  },
  'baking soda': {
    category: 'leavening',
    substitutes: [
      { name: 'baking powder', ratio: '3 tsp per 1 tsp baking soda', notes: 'Need acid in recipe' },
      { name: 'potassium bicarbonate', ratio: '1:1', notes: 'Sodium-free option' },
    ],
  },

  // VEGETABLES
  tomato: {
    category: 'vegetable',
    substitutes: [
      { name: 'canned tomatoes', ratio: '1 cup per 2 fresh', notes: 'Drain first' },
      { name: 'sun-dried tomatoes', ratio: '1/2 cup soaked', notes: 'Concentrated flavor' },
      { name: 'red bell pepper', ratio: '1:1 roasted', notes: 'Sweet, less acid' },
      { name: 'tomatillo', ratio: '1:1', notes: 'Tarter, green' },
    ],
  },
  'cherry tomato': {
    category: 'vegetable',
    substitutes: [
      { name: 'grape tomatoes', ratio: '1:1' },
      { name: 'canned diced + drained', ratio: '1 cup per 1 cup' },
    ],
  },
  'canned tomatoes': {
    category: 'vegetable',
    substitutes: [
      { name: 'fresh tomatoes', ratio: '2 fresh per 1 cup', notes: 'Peel, seed, simmer' },
      { name: 'tomato sauce + water', ratio: '3/4 cup sauce + 1/4 cup water' },
    ],
  },
  'tomato paste': {
    category: 'vegetable',
    substitutes: [
      { name: 'tomato sauce reduced', ratio: '3 tbsp per 1 tbsp', notes: 'Reduce by half' },
      { name: 'ketchup', ratio: '1:1', notes: 'Sweeter, reduce sugar' },
    ],
  },
  'tomato sauce': {
    category: 'vegetable',
    substitutes: [
      { name: 'canned tomatoes blended', ratio: '1:1' },
      { name: 'tomato paste + water', ratio: '1/3 cup paste + 2/3 cup water' },
    ],
  },
  potato: {
    category: 'vegetable',
    substitutes: [
      { name: 'sweet potato', ratio: '1:1', notes: 'Sweeter, softer' },
      { name: 'cauliflower', ratio: '1:1', notes: 'Low carb, mash works' },
      { name: 'turnip', ratio: '1:1', notes: 'Slightly bitter' },
      { name: 'parsnip', ratio: '1:1', notes: 'Sweeter' },
    ],
  },
  'sweet potato': {
    category: 'vegetable',
    substitutes: [
      { name: 'butternut squash', ratio: '1:1', notes: 'Similar texture' },
      { name: 'pumpkin', ratio: '1:1' },
      { name: 'carrot', ratio: '1:1', notes: 'Less starchy' },
    ],
  },
  carrot: {
    category: 'vegetable',
    substitutes: [
      { name: 'parsnip', ratio: '1:1', notes: 'Earthier' },
      { name: 'sweet potato', ratio: '1:1', notes: 'Sweeter' },
      { name: 'butternut squash', ratio: '1:1' },
    ],
  },
  celery: {
    category: 'vegetable',
    substitutes: [
      { name: 'fennel stalks', ratio: '1:1', notes: 'Anise note' },
      { name: 'bok choy stems', ratio: '1:1' },
      { name: 'jicama', ratio: '1:1', notes: 'Crunchier, sweeter' },
    ],
  },
  'bell pepper': {
    category: 'vegetable',
    substitutes: [
      { name: 'poblano', ratio: '1:1', notes: 'Mild heat' },
      { name: 'cubanelle', ratio: '1:1', notes: 'Mild' },
      { name: 'tomato', ratio: '1:1 chopped', notes: 'For stews' },
    ],
  },
  'jalapeño': {
    category: 'vegetable',
    substitutes: [
      { name: 'serrano', ratio: '1:2', notes: 'Hotter, use less' },
      { name: 'poblano', ratio: '2:1', notes: 'Milder' },
      { name: 'red pepper flakes', ratio: '1/4 tsp per pepper' },
    ],
  },
  'chili pepper': {
    category: 'vegetable',
    substitutes: [
      { name: 'red pepper flakes', ratio: '1/4 tsp per pepper' },
      { name: 'cayenne', ratio: '1/8 tsp per pepper' },
      { name: 'hot sauce', ratio: '1 tsp per pepper' },
    ],
  },
  spinach: {
    category: 'vegetable',
    substitutes: [
      { name: 'kale', ratio: '1:1 chopped', notes: 'Heartier' },
      { name: 'swiss chard', ratio: '1:1' },
      { name: 'arugula', ratio: '1:1', notes: 'Peppery, no cook' },
    ],
  },
  kale: {
    category: 'vegetable',
    substitutes: [
      { name: 'swiss chard', ratio: '1:1' },
      { name: 'collard greens', ratio: '1:1' },
      { name: 'spinach', ratio: '1:1', notes: 'Softer' },
    ],
  },
  lettuce: {
    category: 'vegetable',
    substitutes: [
      { name: 'arugula', ratio: '1:1', notes: 'Peppery' },
      { name: 'spinach', ratio: '1:1' },
      { name: 'cabbage', ratio: '1:1 shredded', notes: 'Crunchier' },
    ],
  },
  cabbage: {
    category: 'vegetable',
    substitutes: [
      { name: 'bok choy', ratio: '1:1' },
      { name: 'napa cabbage', ratio: '1:1', notes: 'Milder' },
      { name: 'kale', ratio: '1:1', notes: 'Heartier' },
    ],
  },
  broccoli: {
    category: 'vegetable',
    substitutes: [
      { name: 'cauliflower', ratio: '1:1' },
      { name: 'broccolini', ratio: '1:1' },
      { name: 'asparagus', ratio: '1:1' },
    ],
  },
  cauliflower: {
    category: 'vegetable',
    substitutes: [
      { name: 'broccoli', ratio: '1:1' },
      { name: 'romanesco', ratio: '1:1' },
      { name: 'turnip', ratio: '1:1' },
    ],
  },
  zucchini: {
    category: 'vegetable',
    substitutes: [
      { name: 'yellow squash', ratio: '1:1' },
      { name: 'eggplant', ratio: '1:1', notes: 'Denser' },
      { name: 'cucumber', ratio: '1:1', notes: 'For raw uses' },
    ],
  },
  eggplant: {
    category: 'vegetable',
    substitutes: [
      { name: 'zucchini', ratio: '1:1', notes: 'Less meaty' },
      { name: 'portobello mushroom', ratio: '1:1' },
      { name: 'tofu', ratio: '1:1 firm', notes: 'For stir-fries' },
    ],
  },
  cucumber: {
    category: 'vegetable',
    substitutes: [
      { name: 'zucchini', ratio: '1:1', notes: 'For cooked' },
      { name: 'jicama', ratio: '1:1', notes: 'Crunchier' },
      { name: 'celery', ratio: '1:1', notes: 'Less juicy' },
    ],
  },
  mushrooms: {
    category: 'vegetable',
    substitutes: [
      { name: 'eggplant', ratio: '1:1', notes: 'Meaty' },
      { name: 'zucchini', ratio: '1:1' },
      { name: 'tofu', ratio: '1:1 cubed', notes: 'For umami' },
    ],
  },
  corn: {
    category: 'vegetable',
    substitutes: [
      { name: 'frozen peas', ratio: '1:1' },
      { name: 'edamame', ratio: '1:1' },
      { name: 'hominy', ratio: '1:1' },
    ],
  },
  peas: {
    category: 'vegetable',
    substitutes: [
      { name: 'edamame', ratio: '1:1' },
      { name: 'lima beans', ratio: '1:1' },
      { name: 'corn', ratio: '1:1' },
    ],
  },
  'green beans': {
    category: 'vegetable',
    substitutes: [
      { name: 'asparagus', ratio: '1:1' },
      { name: 'snow peas', ratio: '1:1' },
      { name: 'broccolini', ratio: '1:1' },
    ],
  },
  asparagus: {
    category: 'vegetable',
    substitutes: [
      { name: 'green beans', ratio: '1:1' },
      { name: 'broccolini', ratio: '1:1' },
      { name: 'zucchini', ratio: '1:1 sliced' },
    ],
  },
  leek: {
    category: 'aromatic',
    substitutes: [
      { name: 'shallots', ratio: '2:1', notes: 'Stronger' },
      { name: 'onion', ratio: '1:1', notes: 'Sharper' },
      { name: 'green onion white parts', ratio: '1:1' },
    ],
  },
  scallions: {
    category: 'aromatic',
    substitutes: [
      { name: 'chives', ratio: '1:1', notes: 'Milder' },
      { name: 'shallots', ratio: '1:2' },
      { name: 'leek', ratio: '1:2' },
    ],
  },
  ginger: {
    category: 'aromatic',
    substitutes: [
      { name: 'galangal', ratio: '1:1', notes: 'Sharper, citrusy' },
      { name: 'ground ginger', ratio: '1/4 tsp per 1 tbsp fresh' },
      { name: 'allspice + cardamom', ratio: 'pinch each' },
    ],
  },
  avocado: {
    category: 'vegetable',
    substitutes: [
      { name: 'mashed peas', ratio: '1:1', notes: 'For spreads' },
      { name: 'hummus', ratio: '1:1', notes: 'For spreads' },
      { name: 'silken tofu', ratio: '1:1 blended', notes: 'For creamy uses' },
    ],
  },
  olives: {
    category: 'vegetable',
    substitutes: [
      { name: 'capers', ratio: '1:1 chopped', notes: 'Saltier' },
      { name: 'pickles', ratio: '1:1 chopped' },
      { name: 'sun-dried tomatoes', ratio: '1:1', notes: 'Different but briny' },
    ],
  },
  capers: {
    category: 'condiment',
    substitutes: [
      { name: 'chopped olives', ratio: '1:1' },
      { name: 'pickled green peppercorns', ratio: '1:1' },
      { name: 'chopped pickle', ratio: '1:1' },
    ],
  },

  // HERBS
  basil: {
    category: 'herb',
    substitutes: [
      { name: 'mint + parsley', ratio: '1:1 mixed' },
      { name: 'oregano', ratio: '1/2 dried per 1 fresh' },
      { name: 'dried basil', ratio: '1 tsp per 1 tbsp fresh' },
    ],
  },
  parsley: {
    category: 'herb',
    substitutes: [
      { name: 'cilantro', ratio: '1:1', notes: 'Different flavor' },
      { name: 'chervil', ratio: '1:1' },
      { name: 'basil', ratio: '1:1' },
    ],
  },
  cilantro: {
    category: 'herb',
    substitutes: [
      { name: 'parsley + lime zest', ratio: '1:1 + zest' },
      { name: 'basil + mint', ratio: '1:1 mixed' },
      { name: 'culantro', ratio: '1/2:1', notes: 'Stronger' },
    ],
  },
  mint: {
    category: 'herb',
    substitutes: [
      { name: 'basil', ratio: '1:1' },
      { name: 'tarragon', ratio: '1/2:1' },
      { name: 'dried mint', ratio: '1 tsp per 1 tbsp fresh' },
    ],
  },
  dill: {
    category: 'herb',
    substitutes: [
      { name: 'tarragon', ratio: '1:1' },
      { name: 'fennel fronds', ratio: '1:1' },
      { name: 'parsley', ratio: '1:1', notes: 'Milder' },
    ],
  },
  thyme: {
    category: 'herb',
    substitutes: [
      { name: 'oregano', ratio: '1:1', notes: 'Bolder' },
      { name: 'marjoram', ratio: '1:1' },
      { name: 'dried thyme', ratio: '1 tsp per 1 tbsp fresh' },
    ],
  },
  rosemary: {
    category: 'herb',
    substitutes: [
      { name: 'thyme', ratio: '1:1', notes: 'Softer' },
      { name: 'sage', ratio: '1:1' },
      { name: 'dried rosemary', ratio: '1 tsp per 1 tbsp fresh' },
    ],
  },
  sage: {
    category: 'herb',
    substitutes: [
      { name: 'thyme', ratio: '1:1' },
      { name: 'rosemary', ratio: '1:1' },
      { name: 'poultry seasoning', ratio: '1/2:1' },
    ],
  },
  oregano: {
    category: 'herb',
    substitutes: [
      { name: 'marjoram', ratio: '1:1', notes: 'Milder' },
      { name: 'thyme', ratio: '1:1' },
      { name: 'basil', ratio: '1:1' },
    ],
  },
  'bay leaf': {
    category: 'herb',
    substitutes: [
      { name: 'thyme sprig', ratio: '1:1' },
      { name: 'dried basil', ratio: '1/4 tsp per leaf' },
    ],
  },
  chives: {
    category: 'herb',
    substitutes: [
      { name: 'scallion greens', ratio: '1:1' },
      { name: 'leek greens', ratio: '1:1' },
      { name: 'parsley + onion powder', ratio: '1:1 + pinch' },
    ],
  },

  // SPICES
  cumin: {
    category: 'spice',
    substitutes: [
      { name: 'coriander', ratio: '1:1', notes: 'Milder' },
      { name: 'chili powder', ratio: '1:1', notes: 'Adds heat' },
      { name: 'garam masala', ratio: '1:1' },
    ],
  },
  paprika: {
    category: 'spice',
    substitutes: [
      { name: 'smoked paprika', ratio: '1:1', notes: 'Smoky' },
      { name: 'chili powder', ratio: '1:1', notes: 'Spicier' },
      { name: 'cayenne', ratio: '1/4:1', notes: 'Much hotter' },
    ],
  },
  'smoked paprika': {
    category: 'spice',
    substitutes: [
      { name: 'paprika + dash liquid smoke', ratio: '1:1' },
      { name: 'chipotle powder', ratio: '1/2:1', notes: 'Hotter' },
    ],
  },
  'chili powder': {
    category: 'spice',
    substitutes: [
      { name: 'paprika + cumin + cayenne', ratio: '2:1:1/4' },
      { name: 'cayenne', ratio: '1/4:1' },
      { name: 'taco seasoning', ratio: '1:1' },
    ],
  },
  'curry powder': {
    category: 'spice',
    substitutes: [
      { name: 'garam masala + turmeric', ratio: '2:1' },
      { name: 'cumin + coriander + turmeric', ratio: 'equal parts' },
    ],
  },
  'garam masala': {
    category: 'spice',
    substitutes: [
      { name: 'curry powder', ratio: '1:1', notes: 'Different but close' },
      { name: 'allspice + cardamom + cinnamon', ratio: 'equal parts' },
    ],
  },
  cinnamon: {
    category: 'spice',
    substitutes: [
      { name: 'nutmeg', ratio: '1/2:1' },
      { name: 'allspice', ratio: '1:1' },
      { name: 'cardamom', ratio: '1/2:1' },
    ],
  },
  nutmeg: {
    category: 'spice',
    substitutes: [
      { name: 'mace', ratio: '1:1', notes: 'Same plant' },
      { name: 'cinnamon', ratio: '1:1' },
      { name: 'allspice', ratio: '1:1' },
    ],
  },
  cardamom: {
    category: 'spice',
    substitutes: [
      { name: 'cinnamon + nutmeg', ratio: 'equal parts' },
      { name: 'allspice', ratio: '1:1' },
    ],
  },
  cloves: {
    category: 'spice',
    substitutes: [
      { name: 'allspice', ratio: '1:1' },
      { name: 'nutmeg + cinnamon', ratio: 'equal parts' },
    ],
  },
  allspice: {
    category: 'spice',
    substitutes: [
      { name: 'cinnamon + nutmeg + cloves', ratio: 'equal parts' },
    ],
  },
  turmeric: {
    category: 'spice',
    substitutes: [
      { name: 'curry powder', ratio: '1:1', notes: 'Adds other flavors' },
      { name: 'mustard powder', ratio: '1/2:1', notes: 'For color' },
      { name: 'saffron', ratio: 'pinch', notes: 'For color' },
    ],
  },
  coriander: {
    category: 'spice',
    substitutes: [
      { name: 'cumin', ratio: '1:1' },
      { name: 'garam masala', ratio: '1:1' },
      { name: 'fennel seed', ratio: '1:1' },
    ],
  },
  'mustard powder': {
    category: 'spice',
    substitutes: [
      { name: 'prepared mustard', ratio: '1 tbsp per 1 tsp' },
      { name: 'horseradish', ratio: '1:1' },
      { name: 'turmeric', ratio: '1:1', notes: 'For color only' },
    ],
  },
  'garlic powder': {
    category: 'spice',
    substitutes: [
      { name: 'minced garlic', ratio: '1 clove per 1/8 tsp' },
      { name: 'granulated garlic', ratio: '1:1' },
      { name: 'asafoetida', ratio: 'pinch' },
    ],
  },
  'onion powder': {
    category: 'spice',
    substitutes: [
      { name: 'minced fresh onion', ratio: '1 tbsp per 1 tsp' },
      { name: 'shallot powder', ratio: '1:1' },
      { name: 'chives', ratio: '1 tbsp per 1 tsp' },
    ],
  },
  'italian seasoning': {
    category: 'spice',
    substitutes: [
      { name: 'oregano + basil + thyme', ratio: 'equal parts' },
      { name: 'herbes de Provence', ratio: '1:1' },
    ],
  },
  cayenne: {
    category: 'spice',
    substitutes: [
      { name: 'red pepper flakes', ratio: '1:1' },
      { name: 'hot sauce', ratio: '1/2 tsp per 1/4 tsp' },
      { name: 'paprika + chili powder', ratio: '1:1' },
    ],
  },
  'red pepper flakes': {
    category: 'spice',
    substitutes: [
      { name: 'cayenne', ratio: '1/2:1' },
      { name: 'chili oil', ratio: '1 tsp per 1/4 tsp' },
    ],
  },
  'vanilla extract': {
    category: 'spice',
    substitutes: [
      { name: 'vanilla bean', ratio: '1 inch per 1 tsp' },
      { name: 'almond extract', ratio: '1/2:1', notes: 'Different flavor' },
      { name: 'maple syrup', ratio: '1:1', notes: 'For baking' },
    ],
  },
  'almond extract': {
    category: 'spice',
    substitutes: [
      { name: 'vanilla extract', ratio: '2:1', notes: 'Milder' },
      { name: 'amaretto', ratio: '2:1' },
    ],
  },

  // VINEGARS & CONDIMENTS
  vinegar: {
    category: 'acid',
    substitutes: [
      { name: 'lemon juice', ratio: '1:1', notes: 'Milder' },
      { name: 'lime juice', ratio: '1:1' },
      { name: 'white wine vinegar', ratio: '1:1' },
    ],
  },
  'white vinegar': {
    category: 'acid',
    substitutes: [
      { name: 'apple cider vinegar', ratio: '1:1', notes: 'Fruity' },
      { name: 'lemon juice', ratio: '1:1' },
      { name: 'rice vinegar', ratio: '1:1', notes: 'Milder' },
    ],
  },
  'balsamic vinegar': {
    category: 'acid',
    substitutes: [
      { name: 'red wine vinegar + sugar', ratio: '1 tbsp + 1/2 tsp' },
      { name: 'sherry vinegar', ratio: '1:1' },
      { name: 'red wine reduction', ratio: '1:1' },
    ],
  },
  'apple cider vinegar': {
    category: 'acid',
    substitutes: [
      { name: 'white wine vinegar', ratio: '1:1' },
      { name: 'lemon juice', ratio: '1:1' },
      { name: 'rice vinegar', ratio: '1:1' },
    ],
  },
  'rice vinegar': {
    category: 'acid',
    substitutes: [
      { name: 'white wine vinegar + sugar', ratio: '1 tbsp + pinch' },
      { name: 'apple cider vinegar', ratio: '1:1', notes: 'Sharper' },
    ],
  },
  mustard: {
    category: 'condiment',
    substitutes: [
      { name: 'dijon', ratio: '1:1' },
      { name: 'mustard powder + water', ratio: '1 tsp + 1 tsp per 1 tbsp' },
      { name: 'horseradish', ratio: '1:1' },
    ],
  },
  'dijon mustard': {
    category: 'condiment',
    substitutes: [
      { name: 'yellow mustard', ratio: '1:1', notes: 'Milder' },
      { name: 'whole grain mustard', ratio: '1:1' },
      { name: 'mustard powder + vinegar', ratio: '1 tsp + 1 tsp' },
    ],
  },
  mayonnaise: {
    category: 'condiment',
    substitutes: [
      { name: 'Greek yogurt', ratio: '1:1', notes: 'Tangier' },
      { name: 'sour cream', ratio: '1:1' },
      { name: 'mashed avocado', ratio: '1:1' },
    ],
  },
  ketchup: {
    category: 'condiment',
    substitutes: [
      { name: 'tomato paste + vinegar + sugar', ratio: '2 tbsp + 1 tsp + 1 tsp per 1/4 cup' },
      { name: 'tomato sauce + sugar + vinegar', ratio: '1:1 + adjust' },
    ],
  },
  worcestershire: {
    category: 'condiment',
    substitutes: [
      { name: 'soy sauce + vinegar + sugar', ratio: '1 tbsp + 1/2 tsp + pinch' },
      { name: 'soy sauce', ratio: '1:1', notes: 'Less complex' },
      { name: 'tamari', ratio: '1:1' },
    ],
  },
  'hot sauce': {
    category: 'condiment',
    substitutes: [
      { name: 'sriracha', ratio: '1:1' },
      { name: 'chili paste', ratio: '1:1' },
      { name: 'cayenne + vinegar', ratio: '1/4 tsp + 1 tsp per 1 tbsp' },
    ],
  },
  sriracha: {
    category: 'condiment',
    substitutes: [
      { name: 'hot sauce', ratio: '1:1' },
      { name: 'sambal oelek', ratio: '1:1', notes: 'Chunkier' },
      { name: 'chili-garlic sauce', ratio: '1:1' },
    ],
  },
  tahini: {
    category: 'condiment',
    substitutes: [
      { name: 'sunflower seed butter', ratio: '1:1' },
      { name: 'cashew butter', ratio: '1:1', notes: 'Sweeter' },
      { name: 'peanut butter', ratio: '1:1', notes: 'Different flavor' },
    ],
  },
  hoisin: {
    category: 'condiment',
    substitutes: [
      { name: 'soy sauce + brown sugar', ratio: '1 tbsp + 1 tsp' },
      { name: 'oyster sauce + sugar', ratio: '1:1' },
      { name: 'bbq sauce + soy', ratio: '1 tbsp + 1 tsp' },
    ],
  },
  'oyster sauce': {
    category: 'condiment',
    substitutes: [
      { name: 'hoisin', ratio: '1:1' },
      { name: 'soy sauce + sugar', ratio: '1 tbsp + 1 tsp' },
      { name: 'mushroom sauce', ratio: '1:1', notes: 'Vegan' },
    ],
  },
  miso: {
    category: 'condiment',
    substitutes: [
      { name: 'soy sauce', ratio: '1 tbsp per 1 tbsp', notes: 'Saltier' },
      { name: 'tahini + soy', ratio: '1:1 mixed' },
    ],
  },
  'sesame oil': {
    category: 'fat',
    substitutes: [
      { name: 'peanut oil + sesame seeds', ratio: '1:1 + sprinkle' },
      { name: 'olive oil', ratio: '1:1', notes: 'Different flavor' },
      { name: 'tahini thinned', ratio: '1:1' },
    ],
  },

  // BROTHS & STOCKS
  'chicken broth': {
    category: 'liquid',
    substitutes: [
      { name: 'vegetable broth', ratio: '1:1' },
      { name: 'bouillon cube + water', ratio: '1 cube + 1 cup' },
      { name: 'white wine + water', ratio: '1:1', notes: 'For deglazing' },
    ],
  },
  'vegetable broth': {
    category: 'liquid',
    substitutes: [
      { name: 'chicken broth', ratio: '1:1' },
      { name: 'water + soy sauce', ratio: '1 cup + 1 tsp' },
      { name: 'mushroom broth', ratio: '1:1' },
    ],
  },
  'beef broth': {
    category: 'liquid',
    substitutes: [
      { name: 'chicken broth + soy sauce', ratio: '1 cup + 1 tsp' },
      { name: 'mushroom broth', ratio: '1:1' },
      { name: 'bouillon cube + water', ratio: '1 cube + 1 cup' },
    ],
  },
  stock: {
    category: 'liquid',
    substitutes: [
      { name: 'broth', ratio: '1:1' },
      { name: 'water + bouillon', ratio: '1 cup + 1 cube' },
    ],
  },

  // CHOCOLATE & SWEETS
  chocolate: {
    category: 'sweetener',
    substitutes: [
      { name: 'cocoa powder + butter', ratio: '3 tbsp + 1 tbsp per 1 oz' },
      { name: 'chocolate chips', ratio: '1:1' },
      { name: 'carob', ratio: '1:1', notes: 'Caffeine-free' },
    ],
  },
  'dark chocolate': {
    category: 'sweetener',
    substitutes: [
      { name: 'semisweet chips', ratio: '1:1' },
      { name: 'cocoa + sugar + butter', ratio: '3 tbsp + 1 tbsp + 1 tbsp per 1 oz' },
    ],
  },
  'cocoa powder': {
    category: 'sweetener',
    substitutes: [
      { name: 'melted chocolate', ratio: '1 oz per 3 tbsp', notes: 'Reduce fat in recipe' },
      { name: 'carob powder', ratio: '1:1' },
    ],
  },
  'chocolate chips': {
    category: 'sweetener',
    substitutes: [
      { name: 'chopped chocolate bar', ratio: '1:1' },
      { name: 'cacao nibs', ratio: '1:1', notes: 'Less sweet' },
    ],
  },
  molasses: {
    category: 'sweetener',
    substitutes: [
      { name: 'brown sugar + water', ratio: '3/4 cup + 1/4 cup' },
      { name: 'maple syrup', ratio: '1:1', notes: 'Milder' },
      { name: 'dark corn syrup', ratio: '1:1' },
    ],
  },
  'agave nectar': {
    category: 'sweetener',
    substitutes: [
      { name: 'honey', ratio: '1:1' },
      { name: 'maple syrup', ratio: '1:1' },
      { name: 'simple syrup', ratio: '1:1' },
    ],
  },
  'powdered sugar': {
    category: 'sweetener',
    substitutes: [
      { name: 'blended granulated sugar + cornstarch', ratio: '1 cup + 1 tbsp' },
    ],
  },
  dates: {
    category: 'sweetener',
    substitutes: [
      { name: 'raisins', ratio: '1:1' },
      { name: 'dried figs', ratio: '1:1' },
      { name: 'prunes', ratio: '1:1' },
    ],
  },

  // NUTS & SEEDS
  almonds: {
    category: 'nut',
    substitutes: [
      { name: 'cashews', ratio: '1:1' },
      { name: 'hazelnuts', ratio: '1:1' },
      { name: 'sunflower seeds', ratio: '1:1', notes: 'Nut-free' },
    ],
  },
  'almond flour': {
    category: 'flour',
    substitutes: [
      { name: 'cashew flour', ratio: '1:1' },
      { name: 'hazelnut flour', ratio: '1:1' },
      { name: 'all-purpose flour', ratio: '1:1', notes: 'Adds gluten' },
    ],
  },
  walnuts: {
    category: 'nut',
    substitutes: [
      { name: 'pecans', ratio: '1:1' },
      { name: 'almonds', ratio: '1:1' },
      { name: 'sunflower seeds', ratio: '1:1' },
    ],
  },
  pecans: {
    category: 'nut',
    substitutes: [
      { name: 'walnuts', ratio: '1:1' },
      { name: 'hazelnuts', ratio: '1:1' },
      { name: 'almonds', ratio: '1:1' },
    ],
  },
  cashews: {
    category: 'nut',
    substitutes: [
      { name: 'almonds', ratio: '1:1' },
      { name: 'macadamia nuts', ratio: '1:1' },
      { name: 'sunflower seeds', ratio: '1:1', notes: 'For creams' },
    ],
  },
  'pine nuts': {
    category: 'nut',
    substitutes: [
      { name: 'slivered almonds', ratio: '1:1' },
      { name: 'walnuts', ratio: '1:1' },
      { name: 'sunflower seeds', ratio: '1:1' },
    ],
  },
  'sesame seeds': {
    category: 'seed',
    substitutes: [
      { name: 'poppy seeds', ratio: '1:1' },
      { name: 'hemp seeds', ratio: '1:1' },
      { name: 'sunflower seeds', ratio: '1:1' },
    ],
  },
  'chia seeds': {
    category: 'seed',
    substitutes: [
      { name: 'flax seeds', ratio: '1:1' },
      { name: 'hemp seeds', ratio: '1:1' },
      { name: 'basil seeds', ratio: '1:1' },
    ],
  },
};

/**
 * Common aliases for ingredient lookup
 */
export const ingredientAliases: Record<string, string> = {
  'whole milk': 'milk',
  '2% milk': 'milk',
  'skim milk': 'milk',
  'half and half': 'cream',
  'half & half': 'cream',
  'whipping cream': 'heavy cream',
  'cheddar': 'cheese',
  'mozzarella': 'cheese',
  'swiss cheese': 'cheese',
  'gruyere': 'cheese',
  'feta': 'cheese',
  'ricotta': 'cheese',
  'cottage cheese': 'cheese',
  'cream cheese': 'cheese',
  'parmigiano': 'parmesan',
  'parmigiano-reggiano': 'parmesan',
  'chicken breast': 'chicken',
  'chicken thigh': 'chicken',
  'chicken thighs': 'chicken',
  'chicken leg': 'chicken',
  'chicken wing': 'chicken',
  'ground chicken': 'chicken',
  'beef steak': 'beef',
  'steak': 'beef',
  'ground pork': 'pork',
  'pork chop': 'pork',
  'pork loin': 'pork',
  'white fish': 'fish',
  'cod': 'fish',
  'tilapia': 'fish',
  'halibut': 'fish',
  'sea bass': 'fish',
  'ap flour': 'all-purpose flour',
  'plain flour': 'all-purpose flour',
  'white rice': 'rice',
  'jasmine rice': 'rice',
  'basmati rice': 'rice',
  'brown rice': 'rice',
  'canola oil': 'vegetable oil',
  'corn oil': 'vegetable oil',
  'sunflower oil': 'vegetable oil',
  'extra virgin olive oil': 'olive oil',
  'evoo': 'olive oil',
  'white sugar': 'sugar',
  'granulated sugar': 'sugar',
  'caster sugar': 'sugar',
  'light brown sugar': 'brown sugar',
  'dark brown sugar': 'brown sugar',
  'pure maple syrup': 'maple syrup',
  'raw honey': 'honey',
  'tamari': 'soy sauce',
  'shoyu': 'soy sauce',
  'yellow onion': 'onion',
  'white onion': 'onion',
  'red onion': 'onion',
  'sweet onion': 'onion',
  'vidalia onion': 'onion',
  'garlic clove': 'garlic',
  'garlic cloves': 'garlic',
  'fresh lemon juice': 'lemon juice',
  'dry white wine': 'white wine',
  'dry red wine': 'red wine',
  'corn flour': 'cornstarch',
  'maize starch': 'cornstarch',

  // Tomato family
  'roma tomato': 'tomato',
  'roma tomatoes': 'tomato',
  'plum tomato': 'tomato',
  'plum tomatoes': 'tomato',
  'beefsteak tomato': 'tomato',
  'heirloom tomato': 'tomato',
  'crushed tomatoes': 'canned tomatoes',
  'diced tomatoes': 'canned tomatoes',
  'whole peeled tomatoes': 'canned tomatoes',
  'passata': 'tomato sauce',
  'marinara': 'tomato sauce',
  'marinara sauce': 'tomato sauce',
  'pasta sauce': 'tomato sauce',

  // Onion family extensions
  'green onion': 'scallions',
  'green onions': 'scallions',
  'spring onion': 'scallions',
  'spring onions': 'scallions',

  // Mushroom family
  cremini: 'mushrooms',
  'cremini mushrooms': 'mushrooms',
  portobello: 'mushrooms',
  'portobello mushroom': 'mushrooms',
  'portobello mushrooms': 'mushrooms',
  'button mushroom': 'mushrooms',
  'button mushrooms': 'mushrooms',
  'white mushrooms': 'mushrooms',
  shiitake: 'mushrooms',
  'shiitake mushrooms': 'mushrooms',
  'oyster mushroom': 'mushrooms',
  'oyster mushrooms': 'mushrooms',

  // Pepper family
  'red bell pepper': 'bell pepper',
  'green bell pepper': 'bell pepper',
  'yellow bell pepper': 'bell pepper',
  capsicum: 'bell pepper',
  serrano: 'chili pepper',
  habanero: 'chili pepper',
  'thai chili': 'chili pepper',
  'bird\'s eye chili': 'chili pepper',
  'jalapeno': 'jalapeño',

  // Citrus
  'meyer lemon': 'lemon juice',
  'lime': 'lemon juice',
  'lime juice': 'lemon juice',
  'lemon': 'lemon juice',

  // Lettuce/greens
  romaine: 'lettuce',
  'romaine lettuce': 'lettuce',
  iceberg: 'lettuce',
  'iceberg lettuce': 'lettuce',
  'butter lettuce': 'lettuce',
  bibb: 'lettuce',
  mesclun: 'lettuce',
  'mixed greens': 'lettuce',
  'baby spinach': 'spinach',
  arugula: 'lettuce',
  'swiss chard': 'kale',
  'collard greens': 'kale',

  // Cabbage
  'napa cabbage': 'cabbage',
  'savoy cabbage': 'cabbage',
  'red cabbage': 'cabbage',
  'green cabbage': 'cabbage',

  // Squash family
  'butternut squash': 'sweet potato',
  'acorn squash': 'sweet potato',
  kabocha: 'sweet potato',
  pumpkin: 'sweet potato',
  'yellow squash': 'zucchini',
  'summer squash': 'zucchini',

  // Herbs (variants)
  'flat-leaf parsley': 'parsley',
  'italian parsley': 'parsley',
  'curly parsley': 'parsley',
  'fresh basil': 'basil',
  'thai basil': 'basil',
  'fresh thyme': 'thyme',
  'fresh oregano': 'oregano',
  'fresh rosemary': 'rosemary',
  'fresh sage': 'sage',
  'fresh mint': 'mint',
  'fresh dill': 'dill',
  'fresh chives': 'chives',
  'fresh cilantro': 'cilantro',
  'coriander leaves': 'cilantro',

  // Spices (variants)
  'ground cumin': 'cumin',
  'ground cinnamon': 'cinnamon',
  'ground nutmeg': 'nutmeg',
  'ground cloves': 'cloves',
  'ground coriander': 'coriander',
  'ground ginger': 'ginger',
  'sweet paprika': 'paprika',
  'hot paprika': 'paprika',

  // Nuts
  'slivered almonds': 'almonds',
  'sliced almonds': 'almonds',
  'whole almonds': 'almonds',
  'almond meal': 'almond flour',
  'walnut halves': 'walnuts',
  'chopped walnuts': 'walnuts',
  'raw cashews': 'cashews',

  // Cheeses (extend existing)
  gouda: 'cheese',
  havarti: 'cheese',
  provolone: 'cheese',
  brie: 'cheese',
  manchego: 'cheese',
  'pepper jack': 'cheese',
  'monterey jack': 'cheese',
  goat: 'cheese',
  'goat cheese': 'cheese',
  blue: 'cheese',
  'blue cheese': 'cheese',
  gorgonzola: 'cheese',
  asiago: 'cheese',
  romano: 'parmesan',
  'pecorino romano': 'parmesan',
  pecorino: 'parmesan',

  // Chocolate
  'semisweet chocolate': 'dark chocolate',
  'bittersweet chocolate': 'dark chocolate',
  'milk chocolate': 'chocolate',
  cacao: 'cocoa powder',
  'dutch cocoa': 'cocoa powder',
  'unsweetened cocoa': 'cocoa powder',

  // Vinegars
  'red wine vinegar': 'vinegar',
  'sherry vinegar': 'balsamic vinegar',
  'champagne vinegar': 'white vinegar',
  'distilled vinegar': 'white vinegar',

  // Oils
  'peanut oil': 'vegetable oil',
  'walnut oil': 'olive oil',
  'truffle oil': 'olive oil',
  'avocado oil': 'olive oil',
  'grapeseed oil': 'vegetable oil',

  // Butter & fats
  'salted butter': 'butter',
  'unsalted butter': 'butter',
  'clarified butter': 'butter',
  ghee: 'butter',

  // Broths
  broth: 'chicken broth',
  'low-sodium chicken broth': 'chicken broth',
  'low-sodium vegetable broth': 'vegetable broth',
  'chicken stock': 'chicken broth',
  'vegetable stock': 'vegetable broth',
  'beef stock': 'beef broth',
  bouillon: 'chicken broth',

  // Tomato (continued)
  'cherry tomatoes': 'cherry tomato',
  'grape tomatoes': 'cherry tomato',

  // Misc
  'tomato puree': 'tomato sauce',
  'pizza sauce': 'tomato sauce',
  vanilla: 'vanilla extract',
  'pure vanilla extract': 'vanilla extract',
  'vanilla bean': 'vanilla extract',
};
